import React from "react";
import PropTypes from "prop-types";
import { useNavigate, Link } from "react-router-dom";
import { withSearch, SearchBox } from "@elastic/react-search-ui";


// Reducer objects
import { CHANGE_SITE_INFO } from "../constants/actionTypes";
import { setFirearmSlug } from "../searchui/firearmUrlSync";

// Material UI Components
import { styled, alpha } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import EmailIcon from "@mui/icons-material/Email";
import InfoIcon from "@mui/icons-material/Info";
import SecurityIcon from "@mui/icons-material/Security";
import ListIcon from "@mui/icons-material/List";
import WarningIcon from "@mui/icons-material/Warning";
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import GavelIcon from '@mui/icons-material/Gavel';

// Import logo
import logo from "../assets/logo_light.png";

import { HEADER_HEIGHT as _HEADER_HEIGHT } from "../styles/layoutSizes";
export const HEADER_HEIGHT = _HEADER_HEIGHT;

const Root = styled("div")({
  flexGrow: 0,
  position: "sticky",
  top: 0,
  zIndex: 1100,
});

const StyledToolbar = styled(Toolbar)({
  minHeight: `${HEADER_HEIGHT}px !important`,
  paddingLeft: 12,
  paddingRight: 12,
  gap: 8,
});

const MenuButton = styled(IconButton)({
  padding: 8,
});

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Black Ops One, Roboto",
  fontSize: "1.35rem",
  lineHeight: 1,
  letterSpacing: "0.04em",
  display: "none",
  [theme.breakpoints.up("sm")]: {
    display: "block",
  },
}));

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  flexGrow: 1,
  marginLeft: 8,
  borderRadius: 28,
  backgroundColor: alpha(theme.palette.common.white, 0.18),
  transition: "background-color 160ms ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.26),
  },
  "&:focus-within": {
    backgroundColor: alpha(theme.palette.common.white, 0.32),
  },
  [theme.breakpoints.up("md")]: {
    marginLeft: "auto",
    flexGrow: 0,
    width: 420,
  },
  "& input#searchbar": {
    fontSize: "0.95rem",
    outline: "none",
    color: "#ffffff",
    background: "transparent",
    border: "none",
    padding: theme.spacing(0.75, 1.5, 0.75, 5.5),
    width: "100%",
    "&::placeholder": {
      color: alpha(theme.palette.common.white, 0.7),
    },
    "&:focus": {
      border: "none",
      boxShadow: "none",
    },
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  width: theme.spacing(5),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: alpha(theme.palette.common.white, 0.85),
}));

const LogoLink = styled(Link)({
  display: "flex",
  alignItems: "center",
  color: "#ffffff",
  textDecoration: "none",
  gap: 8,
});

const LogoImg = styled("img")({
  height: 32,
  width: "auto",
  display: "block",
});

const menuPaperSx = {
  marginTop: "8px",
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
};

const RightActions = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 4,
});

const HeaderButton = styled(Button)(({ theme }) => ({
  fontWeight: 800,
  fontSize: "0.8125rem",
  letterSpacing: "0.02em",
  padding: "4px 12px",
  minWidth: 0,
  borderRadius: 999,
  whiteSpace: "nowrap",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

function Header({ setSearchTerm, dispatchSI, disableSearch }) {
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function handleItemSelect(page) {
    setAnchorEl(null);
    dispatchSI({type: CHANGE_SITE_INFO, payload: {open: true, page: page}});
  }

  const handleClearPage = () => {
    setSearchTerm("", { shouldClearFilters: true });
    setFirearmSlug(null, { notify: true });
  };

  const goToDonate = () => {
    var newWindow = window.open();
    newWindow.opener = null;
    newWindow.location = 'https://donorbox.org/support-armalytics';
  };

  return (
    <Root>
      <AppBar position="static" color="primary" elevation={0}>
        <StyledToolbar>
          <MenuButton
            edge="start"
            color="inherit"
            aria-label="open menu"
            onClick={handleClick}
            size="small"
          >
            <MenuIcon />
          </MenuButton>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{ sx: menuPaperSx }}
          >
          <MenuItem onClick={() => handleItemSelect("banMessage")}>
            <ListItemIcon>
              <GavelIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Message on OICs" />
          </MenuItem>
            <MenuItem onClick={() => handleItemSelect("about")}>
              <ListItemIcon>
                <InfoIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="About" />
            </MenuItem>
            <MenuItem onClick={() => handleItemSelect("contact")}>
              <ListItemIcon>
                <EmailIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Contact" />
            </MenuItem>
            <MenuItem onClick={goToDonate}>
              <ListItemIcon>
                <LocalHospitalIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Donate" />
            </MenuItem>
            <MenuItem onClick={() => handleItemSelect("disclaimer")}>
              <ListItemIcon>
                <WarningIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Disclaimer" />
            </MenuItem>
            <MenuItem onClick={() => handleItemSelect("terms")}>
              <ListItemIcon>
                <ListIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Terms & Conditions" />
            </MenuItem>
            <MenuItem onClick={() => handleItemSelect("privacy")}>
              <ListItemIcon>
                <SecurityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Privacy Policy" />
            </MenuItem>
          </Menu>
          <LogoLink to="/" onClick={handleClearPage}>
            <LogoImg src={logo} alt="Armalytics logo" />
            <Title variant="h6" component="h1" noWrap>
              Armalytics
            </Title>
          </LogoLink>
          {!disableSearch ? (
            <Search>
              <SearchIconWrapper>
                <SearchIcon fontSize="small" />
              </SearchIconWrapper>
              <SearchBox
                autocompleteMinimumCharacters={3}
                autocompleteResults={{
                  sectionTitle: "Results",
                  titleField: "model",
                  urlField: "url_field",
                  shouldTrackClickThrough: true,
                  clickThroughTags: ["autoresult"]
                }}
                autocompleteSuggestions={{
                  sectionTitle: "Suggestions",
                }}
                debounceLength={50}
                shouldClearFilters={false}
                inputProps={{id: "searchbar", placeholder: "Search make, model, classification, calibre…"}}
              />
            </Search>
          ) : (
            <>
              <Search />
              <HeaderButton
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => navigate("/")}
                sx={{
                  display: "inline-flex !important",
                  borderColor: "rgba(255,255,255,0.6)",
                  "&:hover": {
                    borderColor: "#fff",
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                FRT
              </HeaderButton>
            </>
          )}

          <RightActions>
            <HeaderButton
              variant="contained"
              color="secondary"
              size="small"
              onClick={goToDonate}
              disableElevation
            >
              Donate
            </HeaderButton>
            <HeaderButton
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => handleItemSelect("subscribe")}
              disableElevation
              sx={{ display: "none" }}
            >
              Subscribe
            </HeaderButton>
          </RightActions>
        </StyledToolbar>
      </AppBar>
    </Root>
  );
}

export default withSearch(({ setSearchTerm }) => ({
  setSearchTerm
}))(Header);

Header.propTypes = {
    children: PropTypes.element,
    setSearchTerm: PropTypes.func,
    dispatchSI: PropTypes.func,
    disableSearch: PropTypes.bool,
};
